/**
 * Link Hub connection metadata and provider action client boundary.
 */
import {
  collection,
  getDocs,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type QueryConstraint,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import { isNoSignInPreviewActive } from '../debug'
import type {
  IntegrationConflict,
  IntegrationConnectionStatus,
  IntegrationProvider,
  IntegrationTarget,
  StorageConnectionMetadata,
  WorkspaceSyncActivity,
} from '../types/schema'
import type { IntegrationProviderId, IntegrationSyncJob, OAuthIntegrationProviderId } from '../types/integration'

export const INTEGRATION_PAGE_SIZE = 20

const callable = <Input, Output>(name: string) => httpsCallable<Input, Output>(firebaseFunctions, name)

export type IntegrationCursor = QueryDocumentSnapshot<DocumentData> | null
export type IntegrationPage<T> = { items: T[]; cursor: IntegrationCursor; hasMore: boolean }

export const loadIntegrationConnections = async (input: {
  workspaceId: string
  provider?: IntegrationProvider | 'ALL'
  status?: IntegrationConnectionStatus | 'ALL'
  cursor?: IntegrationCursor
}): Promise<IntegrationPage<StorageConnectionMetadata>> => {
  const constraints: QueryConstraint[] = [where('workspaceId', '==', input.workspaceId)]
  if (input.provider && input.provider !== 'ALL') constraints.push(where('provider', '==', input.provider))
  else constraints.push(where('provider', 'in', ['NOTION', 'JIRA', 'GOOGLE_CALENDAR', 'GOOGLE_SHEETS']))
  if (input.status && input.status !== 'ALL') constraints.push(where('status', '==', input.status))
  constraints.push(orderBy('createdAt', 'desc'))
  if (input.cursor) constraints.push(startAfter(input.cursor))
  constraints.push(limit(INTEGRATION_PAGE_SIZE + 1))
  const snapshot = await getDocs(query(collection(firestoreDb, 'storage_connections'), ...constraints))
  const visible = snapshot.docs.slice(0, INTEGRATION_PAGE_SIZE)
  return {
    items: visible.map((document) => ({ id: document.id, ...document.data() }) as StorageConnectionMetadata),
    cursor: visible.at(-1) ?? null,
    hasMore: snapshot.size > INTEGRATION_PAGE_SIZE,
  }
}

export const loadIntegrationActivity = async (workspaceId: string, cursor?: IntegrationCursor): Promise<IntegrationPage<WorkspaceSyncActivity>> => {
  const constraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId), orderBy('createdAt', 'desc')]
  if (cursor) constraints.push(startAfter(cursor))
  constraints.push(limit(INTEGRATION_PAGE_SIZE + 1))
  const snapshot = await getDocs(query(collection(firestoreDb, 'workspace_sync_activity'), ...constraints))
  const visible = snapshot.docs.slice(0, INTEGRATION_PAGE_SIZE)
  return {
    items: visible.map((document) => ({ id: document.id, ...document.data() }) as WorkspaceSyncActivity),
    cursor: visible.at(-1) ?? null,
    hasMore: snapshot.size > INTEGRATION_PAGE_SIZE,
  }
}

export const loadOpenIntegrationConflicts = async (workspaceId: string) => {
  const snapshot = await getDocs(query(
    collection(firestoreDb, 'integration_conflicts'),
    where('workspaceId', '==', workspaceId),
    where('status', '==', 'OPEN'),
    orderBy('createdAt', 'desc'),
    limit(100),
  ))
  return snapshot.docs.map((document) => ({ id: document.id, ...document.data() }) as IntegrationConflict)
}

export const startIntegrationOAuth = async (workspaceId: string, provider: IntegrationProvider, connectionId?: string) => {
  const result = await callable<{ workspaceId: string; provider: IntegrationProvider; appOrigin: string; connectionId?: string }, { authorizationUrl: string }>('startOAuthFlow')({
    workspaceId,
    provider,
    appOrigin: window.location.origin,
    connectionId,
  })
  window.location.assign(result.data.authorizationUrl)
}

export const discoverIntegrationTargets = async (connectionId: string) => {
  const result = await callable<{ connectionId: string }, { targets: Array<Record<string, unknown>> }>('discoverIntegrationTargets')({ connectionId })
  return result.data.targets
}

export const configureIntegrationTargets = async (connectionId: string, targets: IntegrationTarget[]) => {
  const result = await callable<{ connectionId: string; targets: IntegrationTarget[] }, { targets: IntegrationTarget[] }>('configureIntegrationTargets')({ connectionId, targets })
  return result.data.targets
}

export const syncIntegrationNow = async (connectionId: string) => {
  await callable<{ connectionId: string }, { queued: boolean }>('syncIntegrationNow')({ connectionId })
}

export const syncAllIntegrationsNow = async (workspaceId: string) => {
  const result = await callable<{ workspaceId: string }, { queued: number }>('syncAllIntegrationsNow')({ workspaceId })
  return result.data.queued
}

export const disconnectIntegration = async (connectionId: string) => {
  await callable<{ connectionId: string }, { disconnected: boolean }>('disconnectIntegration')({ connectionId })
}

export const hardResetIntegrations = async (workspaceId: string, confirmation: string) => {
  await callable<{ workspaceId: string; confirmation: string }, { reset: boolean }>('hardResetIntegrations')({ workspaceId, confirmation })
}

export const resolveIntegrationConflict = async (conflictId: string, resolution: 'OVERWRITE_PROVIDER' | 'ACCEPT_PROVIDER' | 'DISCONNECT_MAPPING') => {
  await callable<{ conflictId: string; resolution: string }, { resolved: boolean }>('resolveIntegrationConflict')({ conflictId, resolution })
}

const generateOAuthUrl = async (workspaceId: string, providerId: OAuthIntegrationProviderId, returnTo?: string) => {
  const result = await callable<
    { workspaceId: string; providerId: OAuthIntegrationProviderId; returnTo?: string },
    { authorizationUrl: string }
  >('generateOAuthUrl')({ workspaceId, providerId, returnTo })
  return result.data.authorizationUrl
}

export const connectZoomWorkspace = async (workspaceId: string): Promise<{ connectionId: string; connected: true }> => {
  const result = await callable<{ workspaceId: string }, { connectionId: string; connected: true }>('connectZoomWorkspace')({ workspaceId })
  return result.data
}

export const registerGitHubInstallation = async (workspaceId: string, installationId: string, repository: string): Promise<{ connectionId: string; connected: true }> => {
  const result = await callable<{ workspaceId: string; installationId: string; repository: string }, { connectionId: string; connected: true }>('registerGitHubInstallation')({ workspaceId, installationId, repository })
  return result.data
}

export const createGitHubIssue = async (workspaceId: string, elementId: string, title: string, body = ''): Promise<{ issueId: string; number: number; url: string }> => {
  const result = await callable<{ workspaceId: string; elementId: string; title: string; body: string }, { issueId: string; number: number; url: string }>('createGitHubIssue')({ workspaceId, elementId, title, body })
  return result.data
}

export const attachFigmaFile = async (workspaceId: string, elementId: string, fileUrl: string): Promise<{ fileKey: string; webhookId: string; embedUrl: string }> => {
  const result = await callable<{ workspaceId: string; elementId: string; fileUrl: string }, { fileKey: string; webhookId: string; embedUrl: string }>('attachFigmaFile')({ workspaceId, elementId, fileUrl })
  return result.data
}

export const IntegrationManager = {
  async initiate(workspaceId: string, providerId: OAuthIntegrationProviderId, returnTo?: string): Promise<void> {
    const authorizationUrl = await generateOAuthUrl(workspaceId, providerId, returnTo)
    window.location.assign(authorizationUrl)
  },

  async disconnect(workspaceId: string, providerId: OAuthIntegrationProviderId): Promise<void> {
    await callable<
      { workspaceId: string; providerId: IntegrationProviderId },
      { disconnected: boolean }
    >('disconnectLinkHubIntegration')({ workspaceId, providerId })
  },

  async saveWebhook(workspaceId: string, url: string, secret: string): Promise<{ connectionId: string; webhookId: string }> {
    const result = await callable<
      { workspaceId: string; providerId: 'GENERIC_WEBHOOK'; url: string; secret: string },
      { connectionId: string; webhookId: string }
    >('saveWebhookConnection')({ workspaceId, providerId: 'GENERIC_WEBHOOK', url, secret })
    return result.data
  },

  async disconnectWebhook(workspaceId: string, connectionId: string): Promise<void> {
    await callable<
      { workspaceId: string; connectionId: string },
      { disconnected: boolean }
    >('disconnectWebhookConnection')({ workspaceId, connectionId })
  },

  async triggerManualSync(workspaceId: string, providerId: 'NOTION' | 'JIRA' | 'GOOGLE_CALENDAR'): Promise<{ jobId: string; status: 'QUEUED' }> {
    const result = await callable<
      { workspaceId: string; providerId: 'NOTION' | 'JIRA' | 'GOOGLE_CALENDAR' },
      { jobId: string; status: 'QUEUED' }
    >('triggerManualSync')({ workspaceId, providerId })
    return result.data
  },
}

export const subscribeIntegrationSyncJob = (jobId: string, onChange: (job: IntegrationSyncJob | null) => void) =>
  onSnapshot(doc(firestoreDb, 'integration_sync_jobs', jobId), (snapshot) => {
    onChange(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } as IntegrationSyncJob : null)
  })

export type IntegrationAvailability = Record<IntegrationProviderId, boolean>

export const loadIntegrationAvailability = async (workspaceId: string): Promise<IntegrationAvailability> => {
  if (isNoSignInPreviewActive()) {
    return { G_DRIVE: true, NOTION: true, JIRA: true, GOOGLE_CALENDAR: true, GOOGLE_SHEETS: true, GENERIC_WEBHOOK: true, SLACK: true, ZOOM: true, DROPBOX: true, GITHUB: true, FIGMA: true, STAYS_PMS: true }
  }
  const result = await callable<
    { workspaceId: string },
    { availability: IntegrationAvailability }
  >('getIntegrationAvailability')({ workspaceId })
  return result.data.availability
}
